import {
  Box,
  Button,
  Flex,
  Heading,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Stack,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Textarea,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  type MarketDialogueQuestionDto,
  type ReceivedMarketDialogueAnswerDto,
  useCommentOnMarketDialogueAnswerMutation,
  useListMarketDialogueAnswersQuery,
} from "../../autogen/bff-api";
import { DocumentsAsOwnerTable } from "../../common/documents/DocumentsAsOwnerTable";
import { useApiError } from "../../common/errors/useApiError";
import { useDisplayDateWithTime } from "../../common/formatting/useDisplayDateWithTime";
import { GivenAnswer } from "./GivenAnswer";
import { MarketDialogueAnswersByQuestion } from "./MarketDialogueAnswersByQuestion";
import { MarketDialogueInsightReport } from "./MarketDialogueInsightReport";

interface Props {
  marketDialogueId: string;
  questions: MarketDialogueQuestionDto[];
}

export const MarketDialogueAnswers = ({ marketDialogueId, questions }: Props) => {
  const { t } = useTranslation();
  const displayer = useApiError();
  const displayDateWithTime = useDisplayDateWithTime();
  const { data, isLoading, error } = useListMarketDialogueAnswersQuery({ marketDialogueId });
  const [openedId, setOpenedId] = useState<string | null>(null);
  const answers = useMemo(
    () => [...(data?.answers ?? [])].sort((a, b) => a.supplierName.localeCompare(b.supplierName)),
    [data],
  );
  const opened = answers.find((answer) => answer.id === openedId);

  return (
    <Box>
      <Heading as="h2" size="sm" mb="1">
        {t("Answers")}
      </Heading>
      {isLoading ? (
        <Flex justify="center" py="8">
          <Spinner color="smPrimary" />
        </Flex>
      ) : error ? (
        displayer.show(error)
      ) : answers.length === 0 ? (
        <Text color="smMuted">{t("No suppliers have answered yet.")}</Text>
      ) : (
        <Stack spacing="3">
          <Text color="smMuted">{t("{{count}} suppliers have answered", { count: answers.length })}</Text>
          <Tabs variant="soft-rounded" colorScheme="teal" size="sm" isLazy lazyBehavior="keepMounted">
            <TabList>
              <Tab>{t("Per question")}</Tab>
              <Tab>{t("Per supplier")}</Tab>
              <Tab>{t("Insight report")}</Tab>
            </TabList>
            <TabPanels>
              <TabPanel px="0">
                <MarketDialogueAnswersByQuestion
                  marketDialogueId={marketDialogueId}
                  questions={questions}
                  answers={answers}
                  canMark={data?.canMark ?? false}
                  onOpen={setOpenedId}
                />
              </TabPanel>
              <TabPanel px="0">
          <Box bg="smBackground" rounded="xl" border="1px solid" borderColor="smBorder" overflow="hidden">
            <TableContainer>
              <Table variant="simple">
                <Thead>
                  <Tr>
                    <Th>{t("Supplier")}</Th>
                    <Th>{t("Sent")}</Th>
                    <Th>{t("Documents")}</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {answers.map((answer) => (
                    <Tr
                      key={answer.id}
                      _hover={{ bg: "smBackgroundSecondary" }}
                      cursor="pointer"
                      onClick={() => setOpenedId(answer.id)}
                    >
                      <Td fontWeight="medium" color="smText">
                        <Button
                          variant="link"
                          color="smText"
                          fontWeight="medium"
                          onClick={(event) => {
                            event.stopPropagation();
                            setOpenedId(answer.id);
                          }}
                        >
                          {answer.supplierName}
                        </Button>
                      </Td>
                      <Td color="smMuted">{displayDateWithTime(answer.sentAt)}</Td>
                      <Td color="smMuted">
                        {answer.answers.flatMap((a) => a.documents ?? []).length + (answer.otherDocuments?.length ?? 0)}
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </TableContainer>
          </Box>
              </TabPanel>
              <TabPanel px="0">
                {data && <MarketDialogueInsightReport marketDialogueId={marketDialogueId} questions={questions} data={data} />}
              </TabPanel>
            </TabPanels>
          </Tabs>
        </Stack>
      )}
      <Modal isOpen={!!opened} onClose={() => setOpenedId(null)} size="4xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{opened?.supplierName}</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb="6">
            {opened && (
              <Stack spacing="8">
                <AnswerDetail answer={opened} questions={questions} sentAt={displayDateWithTime(opened.sentAt)} />
                {data?.canComment && <AnswerComments marketDialogueId={marketDialogueId} answer={opened} />}
              </Stack>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
};

const AnswerDetail = ({
  answer,
  questions,
  sentAt,
}: {
  answer: ReceivedMarketDialogueAnswerDto;
  questions: MarketDialogueQuestionDto[];
  sentAt: string;
}) => {
  const { t } = useTranslation();
  return (
    <Stack spacing="6">
      <Text color="smMuted">{t("Sent {{date}}", { date: sentAt })}</Text>
      <Stack as="ol" spacing="5" pl="5">
        {questions.map((question, index) => (
          <Box as="li" key={question.id ?? index}>
            <Text fontWeight="medium" whiteSpace="pre-wrap">
              {question.text}
            </Text>
            <GivenAnswer question={question} given={answer.answers.find((a) => a.questionId === question.id)} />
          </Box>
        ))}
      </Stack>
      {(answer.otherDocuments?.length ?? 0) > 0 && (
        <Box>
          <Text fontWeight="medium">{t("Other documents")}</Text>
          <Box mt="2">
            <DocumentsAsOwnerTable documents={answer.otherDocuments ?? []} />
          </Box>
        </Box>
      )}
    </Stack>
  );
};

const AnswerComments = ({
  marketDialogueId,
  answer,
}: {
  marketDialogueId: string;
  answer: ReceivedMarketDialogueAnswerDto;
}) => {
  const { t } = useTranslation();
  const { trigger } = useApiError();
  const displayDateWithTime = useDisplayDateWithTime();
  const [text, setText] = useState("");
  const [comment, { isLoading }] = useCommentOnMarketDialogueAnswerMutation();
  const comments = answer.comments ?? [];

  const onComment = async () => {
    try {
      await comment({
        marketDialogueId,
        answerId: answer.id,
        addMarketDialogueAnswerCommentRequest: { text: text.trim() },
      }).unwrap();
      setText("");
    } catch (e) {
      trigger(e);
    }
  };

  return (
    <Stack spacing="3" data-testid="answer-comments">
      <Box>
        <Text fontWeight="medium">{t("Comments from the team")}</Text>
        <Text fontSize="sm" color="smMuted">
          {t("Only the market dialogue team sees these. The supplier never does.")}
        </Text>
      </Box>
      {comments.length === 0 && (
        <Text fontSize="sm" color="smMuted">
          {t("No comments yet")}
        </Text>
      )}
      {comments.map((entry) => (
        <Box key={entry.id} borderLeft="2px solid" borderColor="smBorder" pl="3">
          <Text fontSize="sm" color="smMuted">
            {entry.authorName} · {displayDateWithTime(entry.at)}
          </Text>
          <Text whiteSpace="pre-wrap">{entry.text}</Text>
        </Box>
      ))}
      <Textarea
        value={text}
        onChange={(event) => setText(event.target.value)}
        placeholder={t("Write a comment for the team") ?? undefined}
        maxLength={2000}
      />
      <Box>
        <Button colorScheme="teal" size="sm" onClick={() => void onComment()} isLoading={isLoading} isDisabled={text.trim() === ""}>
          {t("Add comment")}
        </Button>
      </Box>
    </Stack>
  );
};
